import axios from 'axios';
import { ReactNode, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { Anchor, BaseModal, Icon } from '@anytag/shared-atoms';
import { useGenerateUploadReportSignedUrlPromiseQuery } from '@anytag/graphql';
import { useQueryHelper } from '@anytag/shared-hooks';
import { getGoogleStorageUrl } from '@anytag/shared-utils';
import { MIME_ACCEPT } from '@anytag/shared-constants';
import { FileDrop } from '../../file-drop';

export interface UploadCsvPostReportModalProps {
  isOpen: boolean;
  title?: string;
  loading?: boolean;
  onClose: () => void;
  description?: ReactNode;
  onUploadFile: (fileUrl: string, fileName: string) => void;
}

export const UploadCSVCouponModal = ({
  isOpen,
  onClose,
  loading,
  description,
  onUploadFile,
  title = 'Upload CSV file'
}: UploadCsvPostReportModalProps) => {
  const { t, enqueueSnackbar } = useQueryHelper();
  const childRef = useRef<{ requestUpload: () => Promise<void>; newFile: File }>(null);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const { getGenerateUploadReportSignedUrl } = useGenerateUploadReportSignedUrlPromiseQuery();

  const csvTemplateURL = getGoogleStorageUrl('2022-08-04/Manual-upload-template-coupon.csv').toString();

  const handleStoreUploadedFile = async (acceptedFile: File) => {
    try {
      // Generate file url for storing
      const { data } = await getGenerateUploadReportSignedUrl({
        variables: { fileNames: [acceptedFile.name] }
      });
      const signedUrlData = data.generateUploadReportSignedUrl.fileUploadUrls[0];

      if (!signedUrlData) {
        console.error('signedUrlData undefined');
        enqueueSnackbar(t('fileUploadFail'), { variant: 'error' });

        return;
      }

      // Store file
      await axios(signedUrlData.signedUrl, { method: 'PUT', data: acceptedFile });
      const urlObjFromSignedUrl = new URL(signedUrlData.signedUrl);
      // full path url without search
      const uploadFileUrl = urlObjFromSignedUrl.origin + urlObjFromSignedUrl.pathname;

      onUploadFile(uploadFileUrl, signedUrlData.fileName);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(t('fileUploadFail'), { variant: 'error' });
    }
  };

  return (
    <BaseModal
      size="lg"
      title={title}
      open={isOpen}
      onClose={onClose}
      footer={{
        cancelButton: { onClick: onClose },
        submitButton: {
          title: t('Button.Upload'),
          loading: loading,
          disabled: loading || !uploadedFiles.length,
          onClick: async () => {
            if (childRef.current?.newFile) {
              await handleStoreUploadedFile(childRef.current.newFile);
            }
          }
        }
      }}
    >
      <FileDrop
        onDropCallback={setUploadedFiles}
        ref={childRef}
        description={
          <div>
            {description}

            <div>
              <Trans
                i18nKey="Please use Manual upload template for referral codes"
                components={{
                  a: <Anchor variant="blue" className="inline underline" href={csvTemplateURL} />
                }}
              />
            </div>

            <div className="mt-4 flex items-center justify-between text-base">
              <p className="font-semibold">{t('CSV upload')}</p>
              <Anchor variant="blue" href={csvTemplateURL} className="flex items-center underline">
                <Icon icon="download" size="14px" variant="gray" className="mr-1.5" />
                <span>{t('CSV template')}</span>
              </Anchor>
            </div>
          </div>
        }
        notes={[t('General.DragAndDropMaxSize', { maxSize: '10MB' }), t('General.DragAndDropMIME', { MIME: 'CSV' })]}
        multiple={false}
        dropAreaButtonLabel={t('Upload File')}
        dropAreaDescription={t('drop the file and upload')}
        accept={{ ...MIME_ACCEPT.CSV }}
      />
    </BaseModal>
  );
};
